"use client"

import { useState, useEffect } from "react"
import UserBet from "./UserBet"
import { query, collection, where, getDocs } from "firebase/firestore"
import { db } from "../firebase" // Import Firestore instance
import { useAuth } from "../context/AuthContext" // Import useAuth hook for getting current user
import { Trophy } from "lucide-react"
import { useNavigate } from "react-router-dom"

function MyBets({ onCreateBet }) {
  const navigate = useNavigate()
  const [bets, setBets] = useState([])
  const [filteredBets, setFilteredBets] = useState([])
  const [statusFilter, setStatusFilter] = useState("Created")
  const [loading, setLoading] = useState(true)
  const { currentUser } = useAuth()

  useEffect(() => {
    // Fetch all bets when the component mounts
    const fetchBets = async () => {
      try {
        setLoading(true)
        // Query to get bets where user is the creator
        const q = query(collection(db, "ActiveBets"), where("user_id", "==", currentUser.uid))

        // Query to get bets where user is the acceptor
        const q2 = query(collection(db, "ActiveBets"), where("bet_acceptor_uid", "==", currentUser.uid))

        // Execute both queries
        const [querySnapshot1, querySnapshot2] = await Promise.all([getDocs(q), getDocs(q2)])

        const allBets = []

        querySnapshot1.forEach((doc) => {
          const data = doc.data()
          data.id = doc.id
          allBets.push(data)
        })

        querySnapshot2.forEach((doc) => {
          const data = doc.data()
          data.id = doc.id
          allBets.push(data)
        })

        allBets.sort((a, b) => new Date(b.bet_time) - new Date(a.bet_time))

        setBets(allBets)
        setFilteredBets(allBets.filter((bet) => bet.status === "Created")) // Initially show "Created" bets
        setLoading(false)
      } catch (error) {
        console.error("Error fetching bets:", error)
        setLoading(false)
      }
    }

    fetchBets()
  }, [currentUser])

  // Filter bets based on status
  const filterBets = (status) => {
    setFilteredBets([])
    setStatusFilter(status)

    setFilteredBets(
      bets.filter((bet) => bet.status === status).sort((a, b) => new Date(b.bet_time) - new Date(a.bet_time)),
    )
  }

  // Callback to handle bet deletion
  const handleBetDeleted = (betId) => {
    const updatedBets = bets.filter((bet) => bet.id !== betId)
    setBets(updatedBets)
    setFilteredBets(updatedBets.filter((bet) => bet.status === statusFilter))
  }

  return (
    <div className="min-h-screen flex flex-col items-center p-2 md:p-4">
      <div className="w-full max-w-4xl bg-gray-900/30 backdrop-blur-sm p-3 md:p-6 rounded-xl border border-gray-800 shadow-lg">
        {/* Status filter tabs */}
        <div className="flex justify-center mb-6">
          <div className="inline-block rounded-full bg-gray-800/70 p-1">
            <div className="inline-flex rounded-full overflow-hidden">
              <button
                onClick={() => filterBets("Created")}
                className={`px-6 py-2 text-sm font-medium transition-colors duration-200 
                  ${
                    statusFilter === "Created"
                      ? "bg-emerald-500 text-white"
                      : "bg-transparent text-gray-300 hover:text-white"
                  }`}
              >
                Created
              </button>
              <button
                onClick={() => filterBets("Active")}
                className={`px-6 py-2 text-sm font-medium transition-colors duration-200 
                  ${
                    statusFilter === "Active"
                      ? "bg-emerald-500 text-white"
                      : "bg-transparent text-gray-300 hover:text-white"
                  }`}
              >
                Active
              </button>
              <button
                onClick={() => filterBets("Settled")}
                className={`px-6 py-2 text-sm font-medium transition-colors duration-200 
                  ${
                    statusFilter === "Settled"
                      ? "bg-emerald-500 text-white"
                      : "bg-transparent text-gray-300 hover:text-white"
                  }`}
              >
                Settled
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
            <span className="ml-3 text-white">Loading your bets...</span>
          </div>
        ) : filteredBets.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {filteredBets.map((bet) => (
              <UserBet key={bet.uniqueId} bet={bet} onBetDeleted={handleBetDeleted} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="bg-gray-800/70 p-4 rounded-xl inline-block mb-4">
              <Trophy className="w-12 h-12 text-gray-500 mx-auto" />
            </div>
            <h2 className="text-xl font-medium mb-2 text-white">No {statusFilter} Bets</h2>
            <p className="text-gray-400">
              {statusFilter === "Created"
                ? "Created Bets are wagers you posted that are still waiting for someone to accept. You have no pending created bets right now."
                : statusFilter === "Active"
                  ? "You don't have any active bets"
                  : "You don't have any settled bets"}
            </p>
            {statusFilter === "Created" ? (
              <button
                type="button"
                onClick={() => {
                  if (typeof onCreateBet === "function") {
                    onCreateBet()
                  } else {
                    navigate("/")
                  }
                }}
                className="mt-5 inline-flex items-center rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 transition-colors"
              >
                Create Bet
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

export default MyBets

